import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import ContactFormProps, { ContactFormFields, contactFormValidationSchema } from './dto';
import {
    Box,
    CardHeader,
    Divider,
    FormHelperText,
    Grid,
    InputLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Button } from '@mui/material';
import SelectAsyncFormField from 'components/select-async-form-field';
import { mapJobTitlesToSelectOption } from 'common/dto-types/contacts/job-title-dto';
import { mapContactTypeToSelectOption } from 'common/dto-types/contacts/contact-type-dto';
import VenueDto, { mapVenueToAddressSelectOption } from 'common/dto-types/venue-dto';
import { mapContactCategoryToSelectOption } from 'common/dto-types/contacts/contact-category-dto';
import { convertContactFormFieldValuesToDto, getContactFormInitialValues } from './utils';
import { createContact, fetchContactList, getCities, updateContact } from 'pages/contact/api';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { useNavigate } from 'react-router';
import AsyncSelectSearchFormField from 'components/async-select-search-form-field';
import FDCPServices from 'fdcp/fdcp-services';
import { mapFdcpSeasonToTournamentDto } from 'common/dto-types/tournament-dto';
import axiosServices from 'utils/axios';
import { useMemo } from 'react';
import ContactTableColumnConfig, { SearchableContactTableColumnConfig } from 'pages/contact/contact-column-config';
import VenueTableColumnConfig, {
    DefaultVisibleVenueColumns,
    SearchableVenueTableColumnConfig,
} from 'pages/venue-list-page/venue-column-config';
import TeamTableColumnConfig, {
    DefaultVisibleTeamColumns,
    SearchableTeamTableColumnConfig,
} from 'pages/teams-list-page/team-column-config';
import { fetchVenueList } from 'pages/venue-list-page/api';
import TableDialogSelector from 'components/table-dialog-selector';
import { EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import ReactQuillWrapper from 'components/react-quill-wrapper/react-quill-wrapper';
import { fetchTeamList } from 'pages/teams-list-page/api';
import TeamDto from 'common/dto-types/team-dto';

const ContactForm = ({ contact }: ContactFormProps) => {
    const initialValues = getContactFormInitialValues(contact);
    const navigate = useNavigate();

    const onSubmit = async (values: ContactFormFields, { setSubmitting }: FormikHelpers<ContactFormFields>) => {
        const contactDto = convertContactFormFieldValuesToDto(values);

        let data;
        let confirmMessage;
        let failedMessage;
        if (!contact) {
            data = await createContact(contactDto);
            confirmMessage = 'Contact created successfully!';
            failedMessage = 'Failed to create Contact';
        } else {
            data = await updateContact(contactDto);
            confirmMessage = 'Contact updated successfully!';
            failedMessage = 'Failed to update Contact';
        }

        if (data) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: confirmMessage,
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            navigate(-1);
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: failedMessage,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
            setSubmitting(false);
        }
    };

    const initialCategoriesValues = useMemo(
        () => contact?.categories?.map((category) => category.id ?? 0).filter((id) => id !== 0),
        [contact],
    );

    const formik = useFormik({
        initialValues,
        validationSchema: contactFormValidationSchema,
        onSubmit,
    });

    const { isSubmitting, values, errors, touched, isValid, getFieldProps } = formik;

    const getVenueSelectedItemImage = (item: VenueDto) => item.imageUrl || <EnvironmentOutlined />;

    const getTeamSelectedItemImage = (item: TeamDto) => item.pictureUrl || <EnvironmentOutlined />;

    return (
        <FormikProvider value={formik}>
            <Form>
                <CardHeader
                    title="Profile"
                    action={
                        <Button disabled={isSubmitting || !isValid} type="submit" variant="contained">
                            Save
                        </Button>
                    }
                />
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-first-name">First Name</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-first-name"
                                    placeholder="Enter Contact First name"
                                    error={Boolean((touched.firstName || values.firstName) && errors.firstName)}
                                    {...getFieldProps('firstName')}
                                />
                                {(touched.firstName || values.firstName) && errors.firstName && (
                                    <FormHelperText error id="contact-first-name-helper">
                                        {errors.firstName}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-last-name">Last Name</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-last-name"
                                    placeholder="Enter Contact Last name"
                                    error={Boolean((touched.lastName || values.lastName) && errors.lastName)}
                                    {...getFieldProps('lastName')}
                                />
                                {(touched.lastName || values.lastName) && errors.lastName && (
                                    <FormHelperText error id="contact-last-name-helper">
                                        {errors.lastName}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-name">Contact Name</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-name"
                                    placeholder="Enter Contact name"
                                    error={Boolean((touched.contactName || values.contactName) && errors.contactName)}
                                    {...getFieldProps('contactName')}
                                />
                                {(touched.contactName || values.contactName) && errors.contactName && (
                                    <FormHelperText error id="contact-name-helper">
                                        {errors.contactName}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-avatar">Avatar URL</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-avatar"
                                    placeholder="Enter Avatar URL"
                                    error={Boolean((values.avatarUrl || values.avatarUrl) && errors.avatarUrl)}
                                    {...getFieldProps('avatarUrl')}
                                />
                                {(values.avatarUrl || values.avatarUrl) && errors.avatarUrl && (
                                    <FormHelperText error id="contact-avatar">
                                        {errors.avatarUrl}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-secondary-avatar">Secondary Avatar URL</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-secondary-avatar"
                                    placeholder="Enter Secondary Avatar URL"
                                    error={Boolean(
                                        (values.secondaryAvatarUrl || values.secondaryAvatarUrl) &&
                                            errors.secondaryAvatarUrl,
                                    )}
                                    {...getFieldProps('secondaryAvatarUrl')}
                                />
                                {(values.secondaryAvatarUrl || values.secondaryAvatarUrl) &&
                                    errors.secondaryAvatarUrl && (
                                        <FormHelperText error id="contact-secondary-avatar">
                                            {errors.secondaryAvatarUrl}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <SelectAsyncFormField
                                    label="Job Title"
                                    name="jobTitle"
                                    isRequired={false}
                                    initialValues={contact?.jobTitle?.id ?? 0}
                                    source={'jobTitles'}
                                    optionMapperCb={mapJobTitlesToSelectOption}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <SelectAsyncFormField
                                    label="Contact Type"
                                    name="contactType"
                                    isRequired={false}
                                    initialValues={contact?.contactType?.id ?? 0}
                                    source={'contactTypes'}
                                    optionMapperCb={mapContactTypeToSelectOption}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <CardHeader title="Contact Information" />
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-phone">Phone Number</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-phone"
                                    placeholder="Enter Contact Phone number"
                                    error={Boolean((touched.phone || values.phone) && errors.phone)}
                                    {...getFieldProps('phone')}
                                />
                                {(touched.phone || values.phone) && errors.phone && (
                                    <FormHelperText error id="contact-phone-helper">
                                        {errors.phone}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-email">Email</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-email"
                                    placeholder="Enter Email"
                                    error={Boolean((touched.email || values.email) && errors.email)}
                                    {...getFieldProps('email')}
                                />
                                {(touched.email || values.email) && errors.email && (
                                    <FormHelperText error id="contact-email-helper">
                                        {errors.email}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-secondary-email">Secondary Email</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-secondary-email"
                                    placeholder="Enter Email"
                                    error={Boolean(
                                        (touched.secondaryEmail || values.secondaryEmail) && errors.secondaryEmail,
                                    )}
                                    {...getFieldProps('secondaryEmail')}
                                />
                                {(touched.secondaryEmail || values.secondaryEmail) && errors.secondaryEmail && (
                                    <FormHelperText error id="contact-secondary-email-helper">
                                        {errors.secondaryEmail}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <SelectAsyncFormField
                                    label="Address"
                                    name="address"
                                    isRequired={false}
                                    initialValues={contact?.address?.id ?? 0}
                                    source={'venues'}
                                    optionMapperCb={mapVenueToAddressSelectOption}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-location">Location</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-location"
                                    placeholder="Enter Location"
                                    error={Boolean((touched.location || values.location) && errors.location)}
                                    {...getFieldProps('location')}
                                />
                                {(touched.location || values.location) && errors.location && (
                                    <FormHelperText error id="contact-location-helper">
                                        {errors.location}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-preferred-languages">Preferred Languages</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-preferred-languages"
                                    placeholder="Enter Preferred Languages"
                                    error={Boolean(
                                        (touched.preferredLanguages || values.preferredLanguages) &&
                                            errors.preferredLanguages,
                                    )}
                                    {...getFieldProps('preferredLanguages')}
                                />
                                {(touched.preferredLanguages || values.preferredLanguages) &&
                                    errors.preferredLanguages && (
                                        <FormHelperText error id="contact-preferred-languages-helper">
                                            {errors.preferredLanguages}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-note">Note</InputLabel>
                                <ReactQuillWrapper name="note" />
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <CardHeader title="Social Media" />
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-facebook">Facebook</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-facebook"
                                    placeholder="Enter Contact Facebook"
                                    error={Boolean(
                                        (touched.facebookProfileUrl || values.facebookProfileUrl) &&
                                            errors.facebookProfileUrl,
                                    )}
                                    {...getFieldProps('facebookProfileUrl')}
                                />
                                {(touched.facebookProfileUrl || values.facebookProfileUrl) &&
                                    errors.facebookProfileUrl && (
                                        <FormHelperText error id="contact-facebook-helper">
                                            {errors.facebookProfileUrl}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-instagram">Instagram</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-instagram"
                                    placeholder="Enter Instagram"
                                    error={Boolean(
                                        (touched.instagramProfileUrl || values.instagramProfileUrl) &&
                                            errors.instagramProfileUrl,
                                    )}
                                    {...getFieldProps('instagramProfileUrl')}
                                />
                                {(touched.instagramProfileUrl || values.instagramProfileUrl) &&
                                    errors.instagramProfileUrl && (
                                        <FormHelperText error id="contact-instagram-helper">
                                            {errors.instagramProfileUrl}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-twitter">Twitter (X)</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-twitter"
                                    placeholder="Enter Twitter"
                                    error={Boolean(
                                        (touched.twitterProfileUrl || values.twitterProfileUrl) &&
                                            errors.twitterProfileUrl,
                                    )}
                                    {...getFieldProps('twitterProfileUrl')}
                                />
                                {(touched.twitterProfileUrl || values.twitterProfileUrl) &&
                                    errors.twitterProfileUrl && (
                                        <FormHelperText error id="contact-twitter-helper">
                                            {errors.twitterProfileUrl}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-tiktok">Tiktok</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-tiktok"
                                    placeholder="Enter Tiktok"
                                    error={Boolean(
                                        (touched.tiktokProfileUrl || values.tiktokProfileUrl) &&
                                            errors.tiktokProfileUrl,
                                    )}
                                    {...getFieldProps('tiktokProfileUrl')}
                                />
                                {(touched.tiktokProfileUrl || values.tiktokProfileUrl) && errors.tiktokProfileUrl && (
                                    <FormHelperText error id="contact-tiktok-helper">
                                        {errors.tiktokProfileUrl}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-linkedin">Linkedin</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-linkedin"
                                    placeholder="Enter linkedin"
                                    error={Boolean(
                                        (touched.linkedinProfileUrl || values.linkedinProfileUrl) &&
                                            errors.linkedinProfileUrl,
                                    )}
                                    {...getFieldProps('linkedinProfileUrl')}
                                />
                                {(touched.linkedinProfileUrl || values.linkedinProfileUrl) &&
                                    errors.linkedinProfileUrl && (
                                        <FormHelperText error id="contact-linkedin-helper">
                                            {errors.linkedinProfileUrl}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-youtube">Youtube</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-youtube"
                                    placeholder="Enter Youtube"
                                    error={Boolean(
                                        (touched.youtubeProfileUrl || values.youtubeProfileUrl) &&
                                            errors.youtubeProfileUrl,
                                    )}
                                    {...getFieldProps('youtubeProfileUrl')}
                                />
                                {(touched.youtubeProfileUrl || values.youtubeProfileUrl) &&
                                    errors.youtubeProfileUrl && (
                                        <FormHelperText error id="contact-youtube-helper">
                                            {errors.youtubeProfileUrl}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="contact-website">Website</InputLabel>
                                <TextField
                                    fullWidth
                                    id="contact-website"
                                    placeholder="Enter Website"
                                    error={Boolean((touched.websiteUrl || values.websiteUrl) && errors.websiteUrl)}
                                    {...getFieldProps('websiteUrl')}
                                />
                                {(touched.websiteUrl || values.websiteUrl) && errors.websiteUrl && (
                                    <FormHelperText error id="contact-website-helper">
                                        {errors.websiteUrl}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <CardHeader title="Configuration" />
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <SelectAsyncFormField
                                    label="Categories"
                                    name="categories"
                                    initialValues={initialCategoriesValues}
                                    source={'contactCategories'}
                                    optionMapperCb={mapContactCategoryToSelectOption}
                                    multiple
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <AsyncSelectSearchFormField
                                    label="Tournaments"
                                    name="tournaments"
                                    initialValues={contact?.tournaments ?? []}
                                    fetchData={(inputValue) =>
                                        FDCPServices.searchSeasonByName({ searchString: inputValue }).then((seasons) =>
                                            seasons.map((season) => mapFdcpSeasonToTournamentDto(season)),
                                        )
                                    }
                                    getOptionLabel={(option) => `${option.name} - [${option.fdcpId}]`}
                                    isOptionEqualToValue={(option, selectedValue) => {
                                        return option.fdcpId === selectedValue.fdcpId;
                                    }}
                                    filterOptions={(x) => x}
                                    multiple
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <AsyncSelectSearchFormField
                                    label="Host Cities"
                                    name="cities"
                                    initialValues={contact?.cities ?? []}
                                    fetchData={() => getCities()}
                                    getOptionLabel={(option) => `${option.name} - [${option.fdcpId}]`}
                                    isOptionEqualToValue={(option, selectedValue) => {
                                        return option.fdcpId === selectedValue.fdcpId;
                                    }}
                                    filterOptions={(x) => x}
                                    multiple
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <AsyncSelectSearchFormField
                                    label="User Group"
                                    name="userGroups"
                                    initialValues={contact?.userGroups ?? []}
                                    fetchData={(inputValue) =>
                                        axiosServices
                                            .get(
                                                `userGroups${
                                                    inputValue && `?andSpecifications=NameSpecification:${inputValue}`
                                                }`,
                                            )
                                            .then((res) => res.data.elements)
                                    }
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, selectedValue) => {
                                        return option.id === selectedValue.id;
                                    }}
                                    filterOptions={(x) => x}
                                    multiple
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <Typography>Related Contact</Typography>
                                <TableDialogSelector
                                    name="relatedContact"
                                    defaultVisibleColumnConfig={['id', 'contactName', 'firstName', 'lastName']}
                                    defaultTableColumnConfig={ContactTableColumnConfig}
                                    searchableTableColumnConfig={SearchableContactTableColumnConfig}
                                    fetchDataCb={fetchContactList}
                                    getSelectedItemTitle={(item) => {
                                        const firstName = item.firstName || '';
                                        const lastName = item.lastName || '';

                                        return `${firstName} ${lastName}`.trim() || item.contactName || '';
                                    }}
                                    getSelectedItemSubtitle={(item) =>
                                        item.contactType?.name || item.jobTitle?.name || item.contactName || ''
                                    }
                                    getSelectedItemImage={(item) =>
                                        item.avatarUrl || item.secondaryAvatarUrl || <UserOutlined />
                                    }
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <Typography>Assigned Venues</Typography>
                                <TableDialogSelector
                                    name="assignedVenues"
                                    defaultVisibleColumnConfig={DefaultVisibleVenueColumns}
                                    defaultTableColumnConfig={VenueTableColumnConfig}
                                    searchableTableColumnConfig={SearchableVenueTableColumnConfig}
                                    fetchDataCb={fetchVenueList}
                                    multiple
                                    getSelectedItemTitle={(item) => item.name}
                                    getSelectedItemSubtitle={(item) =>
                                        [item.city, item.country].filter(Boolean).join(', ')
                                    }
                                    getSelectedItemImage={getVenueSelectedItemImage}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <Typography>Assigned Teams</Typography>
                                <TableDialogSelector
                                    name="assignedTeams"
                                    defaultVisibleColumnConfig={DefaultVisibleTeamColumns}
                                    defaultTableColumnConfig={TeamTableColumnConfig}
                                    searchableTableColumnConfig={SearchableTeamTableColumnConfig}
                                    fetchDataCb={fetchTeamList}
                                    multiple
                                    getSelectedItemTitle={(item) => item.name}
                                    getSelectedItemSubtitle={(item) => item.abbreviation}
                                    getSelectedItemImage={getTeamSelectedItemImage}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Form>
        </FormikProvider>
    );
};

export default ContactForm;
